import { Form, Input, InputNumber, Select, Switch, Typography } from 'antd'
import {
  scopeDisplayUnit,
  scopeInUnit,
} from 'features/libra/inspectionPlanning/scopeMeasurement'
import { GeckoMeasurementSystem } from 'types'

import { ExecutionColumn, ExecutionRow, ExecutionValue } from './executionTypes'
import {
  asMeasurement,
  sourceMeasurementOptions,
  thicknessUnit,
} from './inspectionResultsModel'

export const ExecutionValueInput = ({
  column,
  value,
  method,
  system,
  sourceZone,
  readonly,
  onChange,
}: {
  column: ExecutionColumn
  value: ExecutionValue
  method: ExecutionRow['basis']['method']
  system: GeckoMeasurementSystem
  sourceZone: Record<string, unknown>
  readonly: boolean
  onChange: (value: ExecutionValue) => void
}) => {
  const scopeType = column.type === 'thickness' ? 'length' : method.scopeType
  const unit =
    column.type === 'thickness'
      ? thicknessUnit(system === GeckoMeasurementSystem.Imperial)
      : scopeType
        ? scopeDisplayUnit(scopeType, system)
        : undefined
  const scope = asMeasurement(value)
  const choices = sourceMeasurementOptions(column, sourceZone)
  const isMeasurement = column.type === 'scope' || column.type === 'thickness'
  return (
    <Form.Item
      label={`${column.label}${isMeasurement && unit ? ` (${unit.name || 'count'})` : ''}`}
      required={column.required}>
      {isMeasurement ? (
        scopeType && unit ? (
          <>
            <InputNumber
              aria-label={column.label}
              min={0}
              precision={scopeType === 'count' ? 0 : undefined}
              value={scopeInUnit(scope, scopeType, unit)}
              disabled={readonly}
              onChange={(num) =>
                onChange(num === null ? undefined : { num, unit: unit.id })
              }
            />
            {scope && scopeInUnit(scope, scopeType, unit) === undefined && (
              <Typography.Paragraph type='danger'>
                Saved units do not match this measurement.
              </Typography.Paragraph>
            )}
            {!readonly && choices.length > 0 && (
              <Select<number>
                aria-label={`Source value for ${column.label}`}
                placeholder='Use source value'
                value={undefined}
                options={choices.map((choice, index) => ({
                  value: index,
                  label: `${choice.label}: ${scopeInUnit(choice.value, 'length', unit)?.toLocaleString('en-GB', { maximumFractionDigits: 4 })} ${unit.name}`,
                }))}
                onChange={(index) => onChange(choices[index].value)}
              />
            )}
          </>
        ) : (
          <Typography.Text type='secondary'>
            Scope units are not yet configured for this method.
          </Typography.Text>
        )
      ) : column.type === 'boolean' ? (
        <Switch
          aria-label={column.label}
          checked={value === true}
          checkedChildren='Yes'
          unCheckedChildren='No'
          disabled={readonly}
          onChange={onChange}
        />
      ) : column.type === 'number' ? (
        <InputNumber
          aria-label={column.label}
          value={typeof value === 'number' ? value : undefined}
          min={column.min}
          max={column.max}
          disabled={readonly}
          onChange={(num) => onChange(num ?? undefined)}
        />
      ) : (
        <Input.TextArea
          aria-label={column.label}
          value={typeof value === 'string' ? value : ''}
          autoSize
          disabled={readonly}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Form.Item>
  )
}
